/**
 * touch_layout.ts — where the on-screen controls sit, and which ones exist.
 *
 * "Rebinding" means something different on a touchscreen. There are no keys to
 * remap; what a player actually needs is to decide WHICH controls are on screen,
 * how big they are and where — because a thumb that cannot reach the jump button
 * is the touch equivalent of a key bound to the wrong finger.
 *
 * Pure: the layout is data, the clamping is arithmetic, and the module draws it.
 * Positions are stored as a FRACTION of the viewport, never pixels, so a layout
 * arranged in portrait survives a rotation and a phone survives a tablet.
 */

/** What an on-screen control does. The stick is the only one that is not a tap. */
export type TouchAction =
  | 'stick' | 'jump' | 'sprint' | 'crouch' | 'attack' | 'guard'
  | 'interact' | 'weaponNext' | 'map' | 'inventory' | 'menu';

export interface TouchControl {
  id: string;
  action: TouchAction;
  /** Centre, as a fraction of the viewport (0..1). */
  fx: number; fy: number;
  /** Diameter in CSS pixels. */
  size: number;
}

/** Readable names + the glyph each control wears. */
export const TOUCH_LABEL: Readonly<Record<TouchAction, { name: string; glyph: string }>> = {
  stick:      { name: 'Move stick', glyph: '' },
  jump:       { name: 'Jump', glyph: '⤒' },
  sprint:     { name: 'Sprint', glyph: '»' },
  crouch:     { name: 'Crouch', glyph: '⤓' },
  attack:     { name: 'Attack', glyph: '✶' },
  guard:      { name: 'Guard', glyph: '⛉' },
  interact:   { name: 'Interact', glyph: '⊙' },
  weaponNext: { name: 'Next weapon', glyph: '⟳' },
  map:        { name: 'Map', glyph: '🗺' },
  inventory:  { name: 'Inventory', glyph: '▤' },
  menu:       { name: 'Options', glyph: '⚙' },
};

export const TOUCH_MIN_SIZE = 40;
export const TOUCH_MAX_SIZE = 200;

/** The layout the game has always shipped: a left stick and a jump button. */
export function defaultTouchLayout(): TouchControl[] {
  return [
    { id: 'stick', action: 'stick', fx: 0.10, fy: 0.78, size: 110 },
    { id: 'jump', action: 'jump', fx: 0.88, fy: 0.80, size: 62 },
    { id: 'attack', action: 'attack', fx: 0.88, fy: 0.62, size: 62 },
    { id: 'interact', action: 'interact', fx: 0.75, fy: 0.80, size: 52 },
  ];
}

const LS = 'atlas:touch-layout';

/** Keep a control fully on screen, and its size sane. A layout saved on a big
 *  tablet must not park a button off the edge of a phone. */
export function clampControl(c: TouchControl, vw: number, vh: number): TouchControl {
  const size = Math.max(TOUCH_MIN_SIZE, Math.min(TOUCH_MAX_SIZE, Math.round(c.size)));
  const halfX = vw > 0 ? (size / 2) / vw : 0;
  const halfY = vh > 0 ? (size / 2) / vh : 0;
  return {
    ...c, size,
    fx: Math.max(halfX, Math.min(1 - halfX, c.fx)),
    fy: Math.max(halfY, Math.min(1 - halfY, c.fy)),
  };
}

/** Validate and repair a stored layout. An unknown action, a duplicate id or a
 *  missing field drops that control rather than breaking the whole layout. */
export function sanitizeLayout(raw: unknown, vw: number, vh: number): TouchControl[] {
  if (!Array.isArray(raw)) return defaultTouchLayout();
  const seen = new Set<string>();
  const out: TouchControl[] = [];
  for (const r of raw as TouchControl[]) {
    if (!r || typeof r !== 'object') continue;
    if (!(r.action in TOUCH_LABEL)) continue;
    if (typeof r.fx !== 'number' || typeof r.fy !== 'number' || typeof r.size !== 'number') continue;
    if (!Number.isFinite(r.fx) || !Number.isFinite(r.fy) || !Number.isFinite(r.size)) continue;
    const id = typeof r.id === 'string' && r.id && !seen.has(r.id) ? r.id : `${r.action}-${out.length}`;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(clampControl({ id, action: r.action, fx: r.fx, fy: r.fy, size: r.size }, vw, vh));
  }
  return out;
}

export function loadTouchLayout(vw: number, vh: number): TouchControl[] {
  try {
    const raw = localStorage.getItem(LS);
    if (!raw) return defaultTouchLayout();
    return sanitizeLayout(JSON.parse(raw), vw, vh);
  } catch { return defaultTouchLayout(); }
}

export function saveTouchLayout(list: readonly TouchControl[]): void {
  try { localStorage.setItem(LS, JSON.stringify(list)); } catch { /* private mode */ }
}

export function resetTouchLayout(): void {
  try { localStorage.removeItem(LS); } catch { /* private mode */ }
}

/** Add a control near the middle-right, where a thumb can reach it. */
export function addControl(list: readonly TouchControl[], action: TouchAction, vw: number, vh: number): TouchControl[] {
  const id = `${action}-${Date.now().toString(36)}`;
  const n = list.filter((c) => c.action !== 'stick').length;
  const c = clampControl({ id, action, fx: 0.86 - (n % 3) * 0.12, fy: 0.42 + Math.floor(n / 3) * 0.12, size: 58 }, vw, vh);
  return [...list, c];
}

export function removeControl(list: readonly TouchControl[], id: string): TouchControl[] {
  return list.filter((c) => c.id !== id);
}

/** Move a control by a pixel delta, keeping it on screen. */
export function moveControl(list: readonly TouchControl[], id: string, dxPx: number, dyPx: number, vw: number, vh: number): TouchControl[] {
  return list.map((c) => (c.id === id ? clampControl({ ...c, fx: c.fx + dxPx / Math.max(1, vw), fy: c.fy + dyPx / Math.max(1, vh) }, vw, vh) : c));
}

export function resizeControl(list: readonly TouchControl[], id: string, delta: number, vw: number, vh: number): TouchControl[] {
  return list.map((c) => (c.id === id ? clampControl({ ...c, size: c.size + delta }, vw, vh) : c));
}

/** Pixel geometry for drawing. */
export function controlRect(c: TouchControl, vw: number, vh: number): { left: number; top: number; size: number } {
  return { left: c.fx * vw - c.size / 2, top: c.fy * vh - c.size / 2, size: c.size };
}
